import { isEmpty, pickBy, mapValues, omitBy } from 'lodash-es'

import { ILocationParams } from '../reducers/reactRouterLocation'
import { encodeLocationParam } from './location/format'
import { escapeRegex } from './formatString'
import { IFlatQuery } from './location/types'

const DEFAULT_ALLOWED_RANGES = 'a-zA-Z0-9'
const DEFAULT_ALLOWED_CHARS = '-_. '

const ALLOWED_FOR_CATEGORY = '/+'
const ALLOWED_FOR_TAG = ':,&+'
const ALLOWED_FOR_PARAM_VALUE = '/+,:&()!'
const ALLOWED_FOR_SEARCH_TERM = '&'
const ALLOWED_FOR_QUERY_PARAM = '/+,%:'
const ALLOWED_FOR_FULL_URL = '/+,:&?=!;'
const CONSECUTIVE_SPACES_REGEXP = / {2,}/g

// Decoded values we allow in parts of URLs:
const decodeCategory = (value: string) =>
  value.replace(/%2F/g, '/').replace(/%3A/g, ':')
const decodeTag = (value: string) =>
  value.replace(/%2C/g, ',').replace(/%3A/g, ':')
const decodeParam = (value: string) => value.replace(/%3A/g, ':')

const sanitizeWithAllowedChars = (
  value: string,
  additionalAllowedChars: string
) => {
  const regexToFilter = new RegExp(
    `[^${DEFAULT_ALLOWED_RANGES}${escapeRegex(
      DEFAULT_ALLOWED_CHARS
    )}${escapeRegex(additionalAllowedChars)}]`,
    'g'
  )

  return (
    value
      // only keeps part of string preceding any forbidden characters
      .split(regexToFilter)[0]
      // consecutive spaces may be causing server side issues
      .replace(CONSECUTIVE_SPACES_REGEXP, ' ')
      .trim()
  )
}

export const sanitizeString = (value: string) =>
  sanitizeWithAllowedChars(value, '')

export const sanitizeCategory = (value: string) =>
  sanitizeWithAllowedChars(value, ALLOWED_FOR_CATEGORY)

export const sanitizeTag = (value: string) =>
  sanitizeWithAllowedChars(value, ALLOWED_FOR_TAG)

export const sanitizeSearchTerm = (value: string) =>
  sanitizeWithAllowedChars(value, ALLOWED_FOR_SEARCH_TERM)

export const sanitizeQueryParamString = (value: string) =>
  sanitizeWithAllowedChars(value, ALLOWED_FOR_PARAM_VALUE)

export const sanitizeQueryParams = (
  params: ILocationParams
): ILocationParams => {
  return mapValues(omitBy(params, isEmpty), value =>
    sanitizeWithAllowedChars(value || '', ALLOWED_FOR_QUERY_PARAM)
  )
}

export const sanitizeLocationParam = (param: string, delimiter = ',') => {
  const sanitizedParam = sanitizeWithAllowedChars(param, '%-+,/')
    .split(delimiter)
    .map(encodeLocationParam)
    .join('+')
  return sanitizedParam
}

export const sanitizeFullUrl = (url: string) =>
  sanitizeWithAllowedChars(url, ALLOWED_FOR_FULL_URL)

export const sanitizeQueries = (queries: IFlatQuery) => {
  const sanitizedQueries: IFlatQuery = {}

  const nonEmptyQueries = pickBy(queries, value => value !== '')

  const sanitizeAndEncode = (
    key: string,
    sanitizeFunc: (input: string) => string,
    decodeFunc: (input: string) => string
  ) => {
    const sanitizedValue = sanitizeFunc(String(nonEmptyQueries[key]))
    if (sanitizedValue !== '') {
      sanitizedQueries[key] = decodeFunc(encodeURIComponent(sanitizedValue))
    }
  }

  Object.keys(nonEmptyQueries).forEach(currentKey => {
    if (currentKey === 'category') {
      sanitizeAndEncode(currentKey, sanitizeCategory, decodeCategory)
    } else if (['tags', 'colors', 'sizes'].includes(currentKey)) {
      sanitizeAndEncode(currentKey, sanitizeTag, decodeTag)
    } else {
      sanitizeAndEncode(currentKey, sanitizeQueryParamString, decodeParam)
    }
  })

  return sanitizedQueries
}
